import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import connectApi from '../../../services/connectApi';
import COPY from './connect-finance-copy';
import { upcomingPayouts } from './upcoming-payout';
import {
  DEFAULT_FILTERS, applyFilters, hasClientFilter, resolveDateWindow,
} from '../../finance/pages/payouts-filter-bar';
import { csvFilename, payoutsCsv } from '../../finance/pages/payouts-export';
import { downloadCsv } from '../../finance/csv';

export const PAGE_SIZE = 8;

// The Payouts list's state — the balance (for the Upcoming rows) and our own
// payouts list, with the filters, the Date window, the page and the CSV —
// lifted out of src/views/finance/pages/payouts.js so the admin's company
// page can draw the same list for a Stripe company (spec 2026-10-20, admin
// company page: Account Activity shows a Stripe company's payouts, §4).
//
// Lifted, not shared yet: payouts.js keeps its own copy of this logic in
// this round (spec §6) and adopts the hook later. When it does, two things
// stay in the page, not here: the `ready` gate (pass `ready ? companyId :
// null` — a null id reads nothing) and the quiet follow after a click in
// Stripe's balances card (STRIPE_CLICK_FOLLOW_MS), which exists because the
// restaurant's card has Pay out; the admin's card grants no payout, so
// nothing on that page needs following.
//
// Reads: GET Connect/Balance?companyid= and GET
// Connect/Finance/Payouts?companyid=&from=&to= — both admit an Admin with
// connect-manage on any company (CanManageConnectAsync's Admin bypass), and
// the restaurant on its own.
const useCompanyPayouts = (companyId) => {
  const copy = COPY.restaurant;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [balance, setBalance] = useState({ data: null, error: '', loading: true });
  const [list, setList] = useState({ data: [], error: '', loading: true });
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const dateWindow = useMemo(() => resolveDateWindow(filters.date), [filters.date]);

  const loadBalance = useCallback(async () => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    setBalance((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const data = connectApi.toConnectBalance(await connectApi.getConnectBalance(companyId));
      if (!mounted.current || id !== requestId.current) return;
      if (!data) throw new Error('balance');
      setBalance({ data, error: '', loading: false });
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      setBalance((prev) => ({ ...prev, error: copy.sections.balance.error, loading: false }));
    }
  }, [companyId, copy]);

  // Its own counter: the balance and the list refresh independently, and a
  // Date change must not let an older list write over a newer one.
  const listRequest = useRef(0);
  const loadList = useCallback(async () => {
    if (!companyId) return;
    listRequest.current += 1;
    const id = listRequest.current;
    setList((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const rows = connectApi.toFinancePayouts(
        await connectApi.getFinancePayouts(companyId, dateWindow.from, dateWindow.to),
      );
      if (!mounted.current || id !== listRequest.current) return;
      setList({ data: rows, error: '', loading: false });
    } catch {
      if (!mounted.current || id !== listRequest.current) return;
      // Never an empty list over a refused read.
      setList({ data: [], error: copy.sections.payoutActivity.error, loading: false });
    }
  }, [companyId, copy, dateWindow.from, dateWindow.to]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  // A new company or a new Date window starts on page 1.
  useEffect(() => {
    setPage(1);
    loadList();
  }, [loadList]);

  // The Upcoming rows (spec 2026-09-22 §F): never enter `filtered`, so the
  // filter bar, the "Showing" counts and the CSV export never see them.
  // Hidden under a Status or Method filter (spec 2026-09-22 §13): they have
  // neither, so a reader who narrowed to Failed or Instant is not shown
  // money that is not a payout yet. The Date window leaves them alone.
  const upcoming = useMemo(
    () => (hasClientFilter(filters) ? [] : upcomingPayouts(balance.data)),
    [balance.data, filters],
  );
  // The list lands before the balance (~0.6s against ~1.4s on staging,
  // 2026-09-24), so the table drew its payouts and then grew Upcoming rows
  // on top of them. It waits for both — on the first read only: a balance
  // already on file, a refused balance read (the list then shows without
  // Upcoming), or a filter that hides Upcoming anyway ends the wait.
  const awaitingUpcoming =
    balance.loading && !balance.data && !balance.error && !hasClientFilter(filters);
  const tableLoading = list.loading || awaitingUpcoming;
  const filtered = useMemo(() => applyFilters(list.data || [], filters), [list.data, filters]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const changeFilters = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);
  const previousPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const nextPage = useCallback(() => setPage((p) => Math.min(pageCount, p + 1)), [pageCount]);

  const exportCsv = useCallback(
    () => downloadCsv(payoutsCsv(filtered), csvFilename(dateWindow)),
    [filtered, dateWindow],
  );

  return {
    filters,
    changeFilters,
    dateWindow,
    balance,
    list,
    upcoming,
    awaitingUpcoming,
    tableLoading,
    filtered,
    shown,
    page,
    pageCount,
    previousPage,
    nextPage,
    loadBalance,
    loadList,
    exportCsv,
  };
};

export default useCompanyPayouts;
